/**
 * What the cloud knows about a venue, and how recently it knew it.
 *
 * The single rule this file exists to hold: **React renders the server's
 * epistemic state and never upgrades stale knowledge into current knowledge.**
 *
 * During MESH ONLY this page is stale by definition. Every judgement — whether
 * contact has decayed, whether a connectivity mode may still be asserted,
 * whether delivery is genuinely complete — was made by the API from facts it
 * owns. Nothing here recomputes any of it, and in particular nothing here
 * decides that some number of minutes means STALE.
 */

export interface VenueReadinessView {
  readonly readiness: 'READY' | 'ATTENTION_REQUIRED' | 'NOT_CONFIGURED';
  readonly freshness: {
    readonly status: 'FRESH' | 'STALE' | 'CRITICALLY_STALE' | 'NOT_APPLICABLE';
    readonly reason: string;
    readonly lastEdgeContactAt: string | null;
    readonly lastSuccessfulIngressAt: string | null;
    readonly ageSeconds: number | null;
    readonly policyVersion: string;
  };
  readonly authority: {
    readonly gatemeshEdgeId: string;
    readonly authorityEpoch: number;
    readonly state: string;
    readonly closureKind: string | null;
    readonly closureBoundary: number | null;
  } | null;
  readonly delivery: {
    readonly cloudReceivedThroughSequence: number;
    readonly lastKnownEdgeSequence: number | null;
    readonly status: 'CURRENT' | 'CURRENT_AS_OF_LAST_CONTACT' | 'BEHIND' | 'UNKNOWN';
  };
  readonly reconciliation: {
    readonly cloudReconciledThroughSequence: number;
    readonly status: 'CURRENT' | 'CATCHING_UP' | 'UNKNOWN';
  };
  readonly currentConnectivityMode: string;
  readonly lastKnown: {
    readonly connectivityMode: string | null;
    readonly backlog: number | null;
    readonly acknowledgedThroughSequence: number | null;
    readonly observedAt: string | null;
  };
  readonly findings: {
    readonly conflicts: number;
    readonly withheldUnverifiableAuthority: number;
    readonly items: ReadonlyArray<{
      readonly kind: string;
      readonly reason: string;
      readonly originatingEpoch: number;
      readonly gatemeshEdgeId: string;
      readonly edgeSequence: number;
      readonly fromPriorAuthority: boolean;
      readonly detectedAt: string;
    }>;
  };
}

/**
 * Wording per server state.
 *
 * A table rather than conditionals, so adding a state forces a decision about
 * what it says instead of quietly falling into someone else's branch.
 */
const FRESHNESS_WORDING: Record<
  VenueReadinessView['freshness']['status'],
  { title: string; detail: string | null; tone: string }
> = {
  FRESH: {
    title: 'Venue Edge reporting normally',
    detail: null,
    tone: 'ok',
  },
  STALE: {
    title: 'Venue Edge is offline from cloud',
    detail: 'Local admissions may have occurred since this view last updated.',
    tone: 'warn',
  },
  CRITICALLY_STALE: {
    title: 'Venue Edge has not reported for an extended period',
    detail: 'Local admissions may have occurred since this view last updated.',
    tone: 'alarm',
  },
  NOT_APPLICABLE: {
    // An epoch that ended is not expected to check in. Calling that "offline"
    // sends an organizer after a venue that shut down correctly.
    title: 'Authority ended',
    detail: 'This Edge is no longer expected to report.',
    tone: 'ok',
  },
};

const DELIVERY_WORDING: Record<VenueReadinessView['delivery']['status'], string> = {
  CURRENT: 'Current',
  // Never shortened. The cursors agree, and the agreement is only as recent as
  // the claim it rests on; sequences beyond the last report may exist at the
  // venue right now.
  CURRENT_AS_OF_LAST_CONTACT: 'Current as of last contact',
  BEHIND: 'Behind',
  UNKNOWN: 'Unknown',
};

const RECONCILIATION_WORDING: Record<VenueReadinessView['reconciliation']['status'], string> = {
  CURRENT: 'Current',
  CATCHING_UP: 'Catching up',
  UNKNOWN: 'Unknown',
};

const MODE_WORDING: Record<string, string> = {
  CLOUD_AND_MESH: 'CLOUD + MESH',
  MESH_ONLY: 'MESH ONLY',
  CLOUD_DIRECT_MESH_UNAVAILABLE: 'MESH UNAVAILABLE',
  ISOLATED_OFFLINE: 'ISOLATED OFFLINE',
  UNKNOWN: 'Unknown',
};

const when = (value: string | null): string => value ?? 'Never';

export function VenueReadiness({ view }: { view: VenueReadinessView }): React.ReactElement {
  if (view.readiness === 'NOT_CONFIGURED' || view.authority === null) {
    return (
      <section data-testid="venue-readiness" data-readiness="NOT_CONFIGURED">
        <h2>Venue authority</h2>
        <p>No GateMesh Edge has been activated for this event.</p>
      </section>
    );
  }

  const freshness = FRESHNESS_WORDING[view.freshness.status];
  const closed = view.authority.closureKind !== null;

  return (
    <section
      data-testid="venue-readiness"
      data-readiness={view.readiness}
      data-freshness={view.freshness.status}
    >
      <h2>Venue authority</h2>

      <div
        data-testid="freshness-banner"
        data-tone={freshness.tone}
        data-reason={view.freshness.reason}
      >
        <strong>{freshness.title}</strong>
        {freshness.detail === null ? null : <p>{freshness.detail}</p>}
        {/* The age is displayed; it is not what decided the state. */}
        <p data-testid="last-contact">
          Last Edge contact: {when(view.freshness.lastEdgeContactAt)}
          {view.freshness.ageSeconds === null
            ? ''
            : ` (${String(Math.floor(view.freshness.ageSeconds / 60))} minutes ago)`}
        </p>
      </div>

      <dl data-testid="authority">
        <dt>Edge</dt>
        <dd>{view.authority.gatemeshEdgeId}</dd>
        <dt>Authority epoch</dt>
        <dd>{String(view.authority.authorityEpoch)}</dd>
        <dt>State</dt>
        <dd>{view.authority.state}</dd>
        {closed ? (
          <>
            <dt>Closure</dt>
            <dd data-testid="closure">
              {view.authority.closureKind} through sequence {String(view.authority.closureBoundary)}
            </dd>
          </>
        ) : null}
      </dl>

      <div data-testid="connectivity">
        <p data-testid="current-mode">
          Current venue connectivity: {MODE_WORDING[view.currentConnectivityMode] ?? 'Unknown'}
        </p>
        {view.lastKnown.connectivityMode === null ? null : (
          <p data-testid="last-known-mode">
            Last reported: {MODE_WORDING[view.lastKnown.connectivityMode] ?? 'Unknown'} at{' '}
            {when(view.lastKnown.observedAt)}
          </p>
        )}
      </div>

      {/* Transport health and integrity are different dimensions, and a page
          that merged them would go green while findings waited for review. */}
      <div data-testid="delivery">
        <h3>Evidence delivery</h3>
        <p data-testid="delivery-status">{DELIVERY_WORDING[view.delivery.status]}</p>
        <p>
          Cloud received through {String(view.delivery.cloudReceivedThroughSequence)}
          {view.delivery.lastKnownEdgeSequence === null
            ? ''
            : ` · Edge last reported ${String(view.delivery.lastKnownEdgeSequence)}`}
        </p>
      </div>

      <div data-testid="reconciliation">
        <h3>Reconciliation</h3>
        <p data-testid="reconciliation-status">
          {RECONCILIATION_WORDING[view.reconciliation.status]}
        </p>
        <p>
          Reconciled {String(view.reconciliation.cloudReconciledThroughSequence)} /{' '}
          {String(view.delivery.cloudReceivedThroughSequence)} received
        </p>
      </div>

      <div data-testid="last-known">
        <h3>Last reported by the venue</h3>
        {view.lastKnown.backlog === null ? (
          <p>The venue has not reported a backlog.</p>
        ) : (
          <p data-testid="last-known-backlog">
            {/* Never a bare "Backlog: 0". The reassuring number is the one most
                dangerous to promote into a current fact. */}
            Last known backlog: {String(view.lastKnown.backlog)} as of{' '}
            {when(view.lastKnown.observedAt)}
          </p>
        )}
      </div>

      {/* Event-scoped, and deliberately not scoped to the current epoch.
          Readiness above describes whoever holds the partition now; a finding
          records something that happened at this event and did not stop being
          true when authority moved. Scoping these to the current epoch once made
          a retained withholding vanish from this page the moment a replacement
          Edge took over. */}
      <div data-testid="findings">
        <h3>Integrity findings for this event</h3>
        <p data-testid="conflict-count">Admission conflicts: {String(view.findings.conflicts)}</p>
        <p data-testid="withheld-count">
          Withheld unverifiable authority: {String(view.findings.withheldUnverifiableAuthority)}
        </p>
        {view.findings.items.length === 0 ? (
          <p>No integrity findings recorded.</p>
        ) : (
          <ul data-testid="finding-list">
            {view.findings.items.map((finding) => (
              <li
                key={`${finding.kind}-${String(finding.originatingEpoch)}-${String(finding.edgeSequence)}`}
                data-testid="finding"
                data-prior-authority={finding.fromPriorAuthority ? 'true' : 'false'}
              >
                {finding.reason} — Edge sequence {String(finding.edgeSequence)}, authority epoch{' '}
                {String(finding.originatingEpoch)}
                {finding.fromPriorAuthority ? ' (prior authority)' : ''}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
